import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Home, ShoppingBag } from "lucide-react";

const HexGrid = () => (
  <svg
    viewBox="0 0 400 300"
    className="w-full h-full opacity-20"
    aria-hidden="true"
  >
    {[
      [80, 60], [160, 60], [240, 60], [320, 60],
      [40, 120], [120, 120], [200, 120], [280, 120], [360, 120],
      [80, 180], [160, 180], [240, 180], [320, 180],
      [40, 240], [120, 240], [200, 240], [280, 240], [360, 240],
    ].map(([cx, cy], i) => (
      <polygon
        key={i}
        points={`${cx},${cy - 28} ${cx + 24},${cy - 14} ${cx + 24},${cy + 14} ${cx},${cy + 28} ${cx - 24},${cy + 14} ${cx - 24},${cy - 14}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-primary"
      />
    ))}
  </svg>
);

const NotFound = () => {
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">

      {/* Hex grid background */}
      <div className="absolute inset-0 pointer-events-none">
        <HexGrid />
      </div>

      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]" style={{ background: "radial-gradient(circle, hsl(41 95% 57% / 0.06) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 text-center px-6 max-w-lg mx-auto">

        {/* Bee icon */}
        <div className="relative inline-block mb-6">
          <div className="text-8xl animate-bounce" style={{ animationDuration: "2s" }}>
            🐝
          </div>
        </div>

        {/* 404 */}
        <div className="text-[9rem] font-black leading-none text-gradient-brand mb-4 select-none">
          404
        </div>

        {/* Title & desc */}
        <h1 className="text-2xl font-bold mb-3">{t("notFound.title")}</h1>
        <p className="text-muted-foreground mb-10 leading-relaxed">
          {t("notFound.desc")}
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <LocalizedLink
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-gradient-brand text-primary-foreground font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition-all hover:scale-[1.02] shadow-lg"
          >
            <Home className="w-4 h-4" />
            {t("notFound.home")}
          </LocalizedLink>
          <LocalizedLink
            to="/products"
            className="inline-flex items-center justify-center gap-2 border border-border text-foreground font-semibold px-6 py-3 rounded-xl hover:border-primary/50 hover:text-primary transition-all"
          >
            <ShoppingBag className="w-4 h-4" />
            {t("notFound.products")}
          </LocalizedLink>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
